import { useState, useEffect } from "react";
import {
    Box,
    Card,
    Typography,
    Grid,
    Stack,
    CircularProgress,
    Avatar,
    alpha,
} from "@mui/material";
import PeopleAltRoundedIcon from "@mui/icons-material/PeopleAltRounded";
import AccountBalanceWalletRoundedIcon from "@mui/icons-material/AccountBalanceWalletRounded";
import CreditCardRoundedIcon from "@mui/icons-material/CreditCardRounded";
import SavingsRoundedIcon from "@mui/icons-material/SavingsRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import { adminDashboard } from "../api";

const ROLE_COLORS = [
    "#1c6b8c",
    "#2f8fb5",
    "#a855f7",
    "#0ea5e9",
    "#06b6d4",
    "#14b8a6",
    "#22c55e",
    "#f59e0b",
    "#f97316",
];

export default function AdminDashboard() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        adminDashboard()
            .then(({ data }) => setData(data))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    if (loading)
        return (
            <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
                <CircularProgress />
            </Box>
        );

    if (!data)
        return (
            <Box sx={{ py: 10, textAlign: "center" }}>
                <Typography color="text.secondary">
                    Failed to load admin dashboard
                </Typography>
            </Box>
        );

    const stats = [
        {
            label: "Total Users",
            value: data.totalUsers ?? 0,
            icon: <PeopleAltRoundedIcon />,
            color: "#1c6b8c",
        },
        {
            label: "Active Users",
            value: data.activeUsers ?? 0,
            icon: <TrendingUpRoundedIcon />,
            color: "#22c55e",
        },
        {
            label: "Total Payins",
            value: `₹${Number(data.totalPayinVolume ?? data.totalPayinAmount ?? 0).toLocaleString("en-IN")}`,
            icon: <CreditCardRoundedIcon />,
            color: "#0ea5e9",
        },
        {
            label: "Total Payouts",
            value: `₹${Number(data.totalPayoutAmount ?? 0).toLocaleString("en-IN")}`,
            icon: <SavingsRoundedIcon />,
            color: "#f59e0b",
        },
        {
            label: "Pending Payouts",
            value:
                data.pendingPayoutsCount ??
                (data.pendingPayouts && typeof data.pendingPayouts === "object"
                    ? data.pendingPayouts.count
                    : 0) ??
                0,
            icon: <BarChartRoundedIcon />,
            color: "#ef4444",
        },
        {
            label: "Total Wallets",
            value: `₹${Number(data.totalBalance ?? data.totalWalletBalance ?? 0).toLocaleString("en-IN")}`,
            icon: <AccountBalanceWalletRoundedIcon />,
            color: "#2f8fb5",
        },
    ];

    return (
        <Box>
            <Typography variant="h5" mb={0.5}>
                Admin Dashboard
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
                System-wide overview and statistics
            </Typography>

            <Grid container spacing={2} mb={4}>
                {stats.map((s, i) => (
                    <Grid item xs={6} sm={4} md={2} key={i}>
                        <Card sx={{ p: 2, height: "100%" }}>
                            <Stack
                                direction="row"
                                alignItems="center"
                                gap={1.5}
                            >
                                <Avatar
                                    sx={{
                                        bgcolor: alpha(s.color, 0.14),
                                        color: s.color,
                                        width: 40,
                                        height: 40,
                                    }}
                                >
                                    {s.icon}
                                </Avatar>
                                <Box>
                                    <Typography
                                        variant="caption"
                                        color="text.secondary"
                                        noWrap
                                    >
                                        {s.label}
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        fontWeight={700}
                                        fontSize={18}
                                    >
                                        {s.value}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* User Breakdown by Role */}
            {data.usersByRole && data.usersByRole.length > 0 && (
                <Card sx={{ p: 3 }}>
                    <Typography variant="subtitle1" fontWeight={600} mb={2}>
                        Users by Role
                    </Typography>
                    <Grid container spacing={2}>
                        {data.usersByRole.map((r, i) => (
                            <Grid
                                item
                                xs={6}
                                sm={4}
                                md={3}
                                key={r.role_name ?? r.roleName ?? i}
                            >
                                <Stack
                                    direction="row"
                                    alignItems="center"
                                    gap={1.5}
                                    sx={{
                                        p: 1.5,
                                        bgcolor: "action.hover",
                                        borderRadius: 2,
                                    }}
                                >
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: "50%",
                                            bgcolor:
                                                ROLE_COLORS[
                                                    i % ROLE_COLORS.length
                                                ],
                                        }}
                                    />
                                    <Box>
                                        <Typography
                                            variant="body2"
                                            fontWeight={600}
                                        >
                                            {Number(r.count) ?? 0}
                                        </Typography>
                                        <Typography
                                            variant="caption"
                                            color="text.secondary"
                                        >
                                            {(
                                                r.role_name ??
                                                r.roleName ??
                                                ""
                                            ).replace(/_/g, " ")}
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                        ))}
                    </Grid>
                </Card>
            )}
        </Box>
    );
}
